import * as React from "react";
import { pollingTimeMs } from "source/app/presentation/Header/Center/Sync/Helpers/InitializePolling";
import { BuildQuickJournalStoredSnapshot } from "../Data/Stats/QuickJournalStatsCore";
import {
    LoadQuickJournalStoredSnapshotFromServerAsync,
    SaveQuickJournalSnapshotToLocalStorage,
    SaveQuickJournalSnapshotToServerAsync,
} from "../Data/Entries/QuickJournalEntryStorage";
import {
    IQuickJournalStoredSnapshot,
    IQuickJournalState,
} from "../Data/QuickJournalTypes";
import { MergeSyncedSnapshot } from "../Data/Entries/QuickJournalSyncHelpers";

export function useQuickJournalSyncEffects({
    state,
    setState,
    shouldWipeQuickJournalData,
}: {
    state: IQuickJournalState;
    setState: React.Dispatch<React.SetStateAction<IQuickJournalState>>;
    shouldWipeQuickJournalData: boolean;
}) {
    const hasLoadedSyncedEntries = React.useRef(false);
    const syncTimeoutId = React.useRef<number | undefined>(undefined);
    const syncInFlightRef = React.useRef(false);
    const pendingReadRef = React.useRef(false);
    const pendingSaveSnapshotRef = React.useRef<
        IQuickJournalStoredSnapshot | undefined
    >(undefined);
    const flushSyncRef = React.useRef<() => void>(() => undefined);

    React.useEffect(() => {
        if (shouldWipeQuickJournalData) {
            return;
        }

        let isMounted = true;

        const flushSync = () => {
            if (
                !isMounted ||
                document.visibilityState === "hidden" ||
                syncInFlightRef.current
            ) {
                return;
            }

            if (pendingReadRef.current) {
                pendingReadRef.current = false;
                syncInFlightRef.current = true;
                void LoadQuickJournalStoredSnapshotFromServerAsync()
                    .then((remoteSnapshot) => {
                        if (
                            !isMounted ||
                            document.visibilityState === "hidden"
                        ) {
                            return;
                        }

                        hasLoadedSyncedEntries.current = true;
                        setState((currentState) =>
                            MergeSyncedSnapshot(currentState, remoteSnapshot)
                        );
                    })
                    .catch(() => undefined)
                    .finally(() => {
                        syncInFlightRef.current = false;
                        flushSync();
                    });
                return;
            }

            const localSnapshot = pendingSaveSnapshotRef.current;
            if (!localSnapshot) {
                return;
            }

            pendingSaveSnapshotRef.current = undefined;
            syncInFlightRef.current = true;
            void SaveQuickJournalSnapshotToServerAsync(localSnapshot)
                .then((syncedSnapshot) => {
                    if (
                        !isMounted ||
                        document.visibilityState === "hidden"
                    ) {
                        return;
                    }

                    setState((currentState) =>
                        MergeSyncedSnapshot(currentState, syncedSnapshot)
                    );
                })
                .catch(() => undefined)
                .finally(() => {
                    syncInFlightRef.current = false;
                    flushSync();
                });
        };

        flushSyncRef.current = flushSync;

        const requestRead = () => {
            pendingReadRef.current = true;
            flushSync();
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                requestRead();
            }
        };

        requestRead();
        window.addEventListener("focus", requestRead);
        document.addEventListener("visibilitychange", handleVisibilityChange);
        const intervalId = window.setInterval(requestRead, pollingTimeMs);

        return () => {
            isMounted = false;
            flushSyncRef.current = () => undefined;
            pendingReadRef.current = false;
            pendingSaveSnapshotRef.current = undefined;
            window.removeEventListener("focus", requestRead);
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
            window.clearInterval(intervalId);
            if (syncTimeoutId.current !== undefined) {
                window.clearTimeout(syncTimeoutId.current);
                syncTimeoutId.current = undefined;
            }
        };
    }, []);

    React.useEffect(() => {
        if (shouldWipeQuickJournalData) {
            return;
        }

        let localSnapshot: IQuickJournalStoredSnapshot;
        try {
            localSnapshot = SaveQuickJournalSnapshotToLocalStorage(
                BuildQuickJournalStoredSnapshot(
                    state.recentEntries,
                    state.weeklyHourlyCounts
                )
            );
        } catch {
            return;
        }

        if (!hasLoadedSyncedEntries.current) {
            return;
        }

        if (syncTimeoutId.current !== undefined) {
            window.clearTimeout(syncTimeoutId.current);
        }

        syncTimeoutId.current = window.setTimeout(() => {
            pendingSaveSnapshotRef.current = localSnapshot;
            flushSyncRef.current();
            syncTimeoutId.current = undefined;
        }, 750);

        return () => {
            if (syncTimeoutId.current !== undefined) {
                window.clearTimeout(syncTimeoutId.current);
                syncTimeoutId.current = undefined;
            }
        };
    }, [state.recentEntries]);

    return {};
}
